<template>
  <div class="space-y-6">
    <div class="bg-white rounded-xl shadow-sm border border-neutral-200 p-6">
      <div class="flex items-start justify-between gap-4">
        <div>
          <h2 class="text-lg font-bold text-neutral-900">功能板块可见性</h2>
          <p class="text-sm text-neutral-500 mt-1">
            关闭后，该板块会从顶部导航和首页入口隐藏；用户直接输入地址访问也会被拦回首页（数据不受影响，重新打开即可恢复）。
          </p>
        </div>
        <button
          class="px-3 py-1.5 rounded-lg border border-neutral-200 text-xs text-neutral-600 hover:border-primary hover:text-primary shrink-0"
          :disabled="loading"
          @click="load"
        >
          <i class="fa fa-refresh mr-1" :class="{ 'fa-spin': loading }"></i>刷新
        </button>
      </div>

      <div v-if="loading" class="py-12 text-center text-neutral-400">
        <i class="fa fa-circle-o-notch fa-spin text-xl"></i>
      </div>

      <div v-else class="mt-6 space-y-3">
        <div
          v-for="s in sections"
          :key="s.key"
          class="rounded-xl border p-4 flex items-center justify-between gap-4 transition-colors"
          :class="
            s.visible
              ? 'border-green-200 bg-green-50/50'
              : 'border-neutral-200 bg-neutral-50'
          "
        >
          <div class="flex items-start gap-4 min-w-0">
            <div
              class="w-11 h-11 rounded-xl flex items-center justify-center text-lg flex-shrink-0"
              :class="
                s.visible
                  ? 'bg-green-100 text-green-600'
                  : 'bg-neutral-200 text-neutral-400'
              "
            >
              <i :class="['fa', s.icon]"></i>
            </div>
            <div class="min-w-0">
              <div class="flex items-center gap-2 flex-wrap">
                <span class="font-bold text-neutral-900">{{ s.name }}</span>
                <span
                  class="text-xs px-2 py-0.5 rounded-full"
                  :class="
                    s.visible
                      ? 'bg-green-600 text-white'
                      : 'bg-neutral-400 text-white'
                  "
                >
                  {{ s.visible ? "显示中" : "已隐藏" }}
                </span>
              </div>
              <p class="text-xs text-neutral-500 mt-1">{{ s.desc }}</p>
            </div>
          </div>

          <div class="flex items-center gap-2 shrink-0">
            <span class="text-xs text-neutral-400 hidden sm:inline">
              {{ s.visible ? "显示" : "隐藏" }}
            </span>
            <el-switch
              v-model="s.visible"
              :loading="saving === s.key"
              style="--el-switch-on-color: #13ce66; --el-switch-off-color: #9ca3af"
              @change="(v) => onToggle(s, v)"
            />
          </div>
        </div>
      </div>

      <p class="mt-5 text-xs text-neutral-400">
        <i class="fa fa-info-circle mr-1"></i>
        首页「板块区」的显隐另有独立配置，请到「首页设计」里调整。
      </p>
    </div>
  </div>
</template>

<script>
import { ElMessage } from "element-plus";
import { setVisibility } from "@/utils/visibility";

const SECTIONS = [
  {
    key: "square",
    name: "AI应用广场",
    icon: "fa-th-large",
    desc: "/square 聚合页，工具、智能体、需求的统一入口",
  },
  {
    key: "agents",
    name: "AI智能体",
    icon: "fa-robot",
    desc: "/agents 管理员配置的对话式智能体",
  },
  {
    key: "workbench",
    name: "我的AI工作台",
    icon: "fa-window-maximize",
    desc: "/workbench 用户自定义 HTML 工作台",
  },
  {
    key: "create",
    name: "AI创想广场",
    icon: "fa-lightbulb-o",
    desc: "/create 教师创作、需求与案例聚合",
  },
  {
    key: "knowledge",
    name: "知识智能体",
    icon: "fa-book",
    desc: "/knowledge 教师自建知识库与智能体",
  },
  {
    key: "knowledgelink",
    name: "知识链接",
    icon: "fa-link",
    desc: "/knowledge-link 语数英等学科知识点体系",
  },
  {
    key: "software",
    name: "软件下载",
    icon: "fa-download",
    desc: "/software 软件分享与下载",
  },
];

export default {
  name: "VisibilityManager",
  data() {
    return {
      loading: false,
      saving: "",
      sections: SECTIONS.map((s) => ({ ...s, visible: true })),
    };
  },
  mounted() {
    this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        const res = await this.$axios({ url: "visibility", method: "GET" });
        if (res.code === 200 && res.data) {
          this.sections.forEach((s) => {
            if (typeof res.data[s.key] === "boolean") {
              s.visible = res.data[s.key];
            }
          });
        }
      } catch (e) {
        ElMessage.error("读取配置失败");
      }
      this.loading = false;
    },

    async onToggle(s, val) {
      this.saving = s.key;
      try {
        const payload = {};
        this.sections.forEach((x) => {
          payload[x.key] = x.visible;
        });
        const res = await this.$axios({
          url: "visibility/admin/save",
          method: "POST",
          data: { data: await this.$aesEncrypt(payload) },
        });
        if (res.code === 200 && res.data) {
          // 同步前端缓存，当前会话立即生效
          setVisibility(res.data);
          ElMessage.success(`「${s.name}」已${val ? "显示" : "隐藏"}`);
        } else {
          ElMessage.error(res.msg || "保存失败");
          s.visible = !val;
        }
      } catch (e) {
        ElMessage.error("保存失败");
        s.visible = !val;
      }
      this.saving = "";
    },
  },
};
</script>
